import type { SurveyTableItem } from './surveys.types';

// Pending Alerts Widget

export interface PendingAlertsWidgetParams {
  limit?: number;
  accountUuid?: string;
  userUuid?: string; // For manager/executive/driver dashboard
}

export interface PendingAlertsWidgetResponse {
  data: SurveyTableItem[];
  total_count: number;
  no_drivers_assigned?: boolean; // For managers not assigned any drivers
  no_org_level_assigned?: boolean; // For executives not assigned to any org level
  assigned_categories?: string[]; // For staff users with assigned categories
}

// Analytics Widget

// Date range options for analytics data
export enum DateRange {
  ALL_DATA = 'all_data',
  LAST_7_DAYS = 'last_7_days',
  LAST_30_DAYS = 'last_30_days',
}

export interface TrendlineDataPoint {
  date: string; // ISO date string (YYYY-MM-DD)
  value: number;
}

// Survey count metrics (Route Surveys, Survey Comments, Incident Reports)
// For date_range = all_data: only value and last7DaysCount
// For date_range = past 7/ 30 days: value, percentChange, absoluteChange, isIncrease
export interface SurveyCountMetric {
  value: number;
  percentChange?: number; // not for all_data
  absoluteChange?: number; // not for all_data
  isIncrease?: boolean; // not for all_data
  last7DaysCount?: number; // for all_data range only
  trendline?: TrendlineDataPoint[]; // Per-period counts for the bar chart
  trendlinePeriodLabel?: string;
}

export interface SurveyCountMetrics {
  surveyComments: SurveyCountMetric;
  routeSurveys: SurveyCountMetric;
  incidentReports: SurveyCountMetric;
}

// eNPS overall metric (Driver eNPS card with trendline)
export interface EnpsOverallMetric {
  value: number;
  // Change vs previous period (periods) / vs 7 days ago (all_data).
  // absoluteChange is unsigned; direction is in isIncrease.
  percentChange?: number;
  absoluteChange?: number;
  isIncrease?: boolean;
  trendline: TrendlineDataPoint[];
  trendlinePeriodLabel?: string; // e.g., "daily score trend", "weekly score trend", "monthly score trend"
}

// eNPS component metrics (Promoters, Passives, Detractors with pp changes)
export interface EnpsComponentMetric {
  value: number; // Percentage value
  absoluteChange: number; // Percentage point change (pp)
  isIncrease: boolean;
  trendline?: TrendlineDataPoint[]; // Per-period percentages for the bar chart
  trendlinePeriodLabel?: string;
}

// Standard dashboard eNPS metrics (for account/manager/executive dashboards)
export interface StandardEnpsMetrics {
  enps: EnpsOverallMetric;
  promoters: EnpsComponentMetric;
  passives: EnpsComponentMetric;
  detractors: EnpsComponentMetric;
}

// Driver dashboard eNPS metrics (only last eNPS response)
export interface DriverEnpsMetrics {
  lastEnps: EnpsComponentMetric;
}

// Survey score metrics (Overall + Category scores with trendlines)
export interface SurveyScoreMetric {
  value: number;
  // Change vs previous period (periods) / vs 7 days ago (all_data).
  // absoluteChange is unsigned; direction is in isIncrease.
  percentChange?: number;
  absoluteChange?: number;
  isIncrease?: boolean;
  trendline: TrendlineDataPoint[];
  trendlinePeriodLabel?: string; // e.g., "daily score trend", "weekly score trend", "monthly score trend"
}

export interface CategoryScore {
  category: string;
  score: SurveyScoreMetric;
}

export interface SurveyScoreMetrics {
  overallScore: SurveyScoreMetric;
  categoryScores: CategoryScore[];
}

// Bar-chart window boundaries (YYYY-MM-DD), computed server-side so the chart
// renders the exact range the server queried instead of recomputing it from the
// browser clock (which drifts a day off in timezones behind UTC).
export interface AnalyticsWindow {
  start: string | null; // null for all_data (chart anchors to the first data point)
  end: string; // server "today"
}

// Standard dashboard analytics response
export interface StandardAnalyticsWidgetResponse {
  surveyCounts: SurveyCountMetrics;
  enpsMetrics: StandardEnpsMetrics;
  surveyScores: SurveyScoreMetrics;
  window: AnalyticsWindow;
}

// Driver dashboard analytics response
export interface DriverAnalyticsWidgetResponse {
  surveyCounts: SurveyCountMetrics;
  enpsMetrics: DriverEnpsMetrics;
  surveyScores: SurveyScoreMetrics;
  window: AnalyticsWindow;
}

// Union type for any analytics widget response
export type AnalyticsWidgetResponse =
  | StandardAnalyticsWidgetResponse
  | DriverAnalyticsWidgetResponse;

export interface AnalyticsWidgetParams {
  dateRange: DateRange;
  accountUuid?: string;
  userUuid?: string; // For manager/executive/driver dashboard
}

// Organization Widget
export interface OrganizationMember {
  uuid: string;
  name: string;
  organizationLevel: string | null;
  role?: string; // For driver view
  status?: string; // For driver view
  dateAdded?: string; // For driver view
  pendingAlerts?: number; // For account/executive view
  driverCount?: number | null; // For account/executive view
  enps: number | null;
  lastEnps?: number | null; // For drivers: eNPS from their last submitted route survey
  routeSurveys: number | null;
  avgSurveyScore: number | null;
  incidentReports: number | null;
  comments: number | null;
  depth?: number | null; // Hierarchy depth level (0 = top); null for flat/non-hierarchy members
}

export interface OrganizationWidgetResponse {
  data: OrganizationMember[];
  total_count: number;
  no_org_level_assigned?: boolean; // For executives not assigned to any org level
  no_drivers_assigned?: boolean; // For managers not assigned any drivers
}

export interface OrganizationWidgetParams {
  accountUuid?: string;
  userUuid?: string;
}
